import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { Trash2 } from "lucide-react"

import { useModal } from "@/components/core/Root/modal-root"
import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogBodyError,
  DialogBodySuccess,
  DialogBodyWarning,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useLogger } from "@/hooks/useLogger"

import { useMutationWriteRow } from "../upload/no-call-csv-api"
import { NoCallStudent } from "./search-api"

type DialogProps = {
  close: (value: boolean) => void
  student: NoCallStudent
}

export function RemoveNoCallButton({ student }: { student: NoCallStudent }) {
  const { showModal } = useModal()
  const queryClient = useQueryClient()

  async function handleClick() {
    await showModal((close) => (
      <DialogRemoveStudent close={close} student={student} />
    ))
    await queryClient.invalidateQueries({
      queryKey: ["/students/search/global-no-call"],
    })
  }

  return (
    <Button type="button" size="icon" variant="ghost" onClick={handleClick}>
      <Trash2 className="size-4" />
    </Button>
  )
}

type ResultState =
  | { message: string; status: "error" }
  | { message: string; status: "success" }
  | { message: string; status: "warning" }
  | { status: "idle" }

function DialogRemoveStudent({ close, student }: DialogProps) {
  const { t } = useTranslation()
  const log = useLogger()
  // Need separate state because the API can return 200 responses with errors or warnings
  const [result, setResult] = useState<ResultState>({ status: "idle" })

  const writeMutation = useMutationWriteRow({
    onError: (err) => {
      log.error("Failed to remove student from NG list", {
        error: err,
        studentEmail: student.email,
      })

      return setResult({
        status: "error",
        message: t("noCallList.removeStudent.error"),
      })
    },
    onSuccess: (response) => {
      if (response.data.status === "error") {
        log.error("Remove from NG list failed", {
          errorMessage: response.data.message,
          studentEmail: student.email,
        })

        return setResult({
          status: "error",
          message: t("noCallList.removeStudent.error"),
        })
      }

      if (response.data.warnings?.length) {
        log.warn("Remove from NG list completed with warnings", {
          studentEmail: student.email,
          warnings: response.data.warnings,
        })
        const warningKey = response.data.warnings[0]

        return setResult({
          status: "warning",
          message: t(`noCallList.removeStudent.warnings.${warningKey}`, {
            defaultValue: t("noCallList.removeStudent.warnings.INTERNAL_ERROR"),
          }),
        })
      }

      setResult({
        status: "success",
        message: t("noCallList.removeStudent.success"),
      })
    },
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await writeMutation.mutateAsync({
      email: student.email,
      noCall: false,
      phone: student.phone_number,
    })
  }

  return (
    <DialogContent>
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>
            {t("noCallList.removeStudent.confirmTitle")}
          </DialogTitle>
        </DialogHeader>
        {result.status === "idle" && (
          <>
            <DialogBody className="flex flex-col gap-4">
              <p>{t("noCallList.removeStudent.confirmDescription")}</p>
              <div>
                <code
                  className="
                    bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm
                    font-semibold
                  "
                >
                  {student.email}
                </code>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                isDisabled={writeMutation.isPending}
              >
                {t("noCallList.removeStudent.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                isLoading={writeMutation.isPending}
                isDisabled={writeMutation.isPending}
              >
                {t("noCallList.removeStudent.confirmAction")}
              </Button>
            </DialogFooter>
          </>
        )}
        {result.status === "success" && (
          <>
            <DialogBodySuccess>{result.message}</DialogBodySuccess>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(true)}
              >
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
        {result.status === "warning" && (
          <>
            <DialogBodyWarning>{result.message}</DialogBodyWarning>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(true)}
              >
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
        {result.status === "error" && (
          <>
            <DialogBodyError>{result.message}</DialogBodyError>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </form>
    </DialogContent>
  )
}
